/*
 * Cadwork webgl Engine
 * http://www.lexoview.com
 *
 * Copyright (C) 2015 Cadwork Informatik AG, Basel, Switzerland
 * Dual licensed under the MIT and GPL
 *
 * @author Jaroslav Přibyl (pribyl@cadwork.cz)
 *
 */

import RenderObject from "App/geometry/objects/object";
import AbstractGeometry from "App/geometry/objects_geometry/abstract_geometry";
import VertexDataDescriptor from "App/geometry/objects_geometry/vertex_data_descriptor";
import {getBytesPerElementFromType, mergeBuffers} from "App/utils/utils";
import Factory from "App/geometry/objects_geometry/factory";
import {mat4} from "gl-matrix";
import {LineTypeName, OutlineTypeName} from "App/geometry/objects/type_names";

export default
class Binary extends AbstractGeometry {

    public properties: any = {};
    public normalsDesc: VertexDataDescriptor = null;
    public texCoordsDesc: VertexDataDescriptor = null;
    public indicesDesc: VertexDataDescriptor = null;
    public nonIndexedPositionsDesc: VertexDataDescriptor = null;

    public static TypeName: string = "binaryGeometry"; // the name matches the name in geom. desc. json

    constructor(aObject: RenderObject) {
        super(aObject);

        // default values
        this.properties = Binary.getDefaultProperties();
    }

    public static create(aObject: RenderObject) {
        return new Binary(aObject);
    }

    public static getDefaultProperties(): any {
        return {
            src: null,
            vertexCount: 0,
            normalCount: 0,
            texcoordCount: 0,
            indexCount: 0,
            faceSize: [],
            offset: 0,
            binSize: 0
        }
    }

    public clear() {
        super.clear();

        this.normalsDesc = null;
        this.texCoordsDesc = null;
        this.indicesDesc = null;
        this.nonIndexedPositionsDesc = null;
    }

    public static getSubBuffer(aBuffer: ArrayBuffer, aFrom: number, aTo: number): ArrayBuffer {
        if (aBuffer.slice !== undefined) {
            return aBuffer.slice(aFrom, aTo);
        } else {
            let inputBufferAsUInt8Array = new Uint8Array(aBuffer);
            let subdata = new Uint8Array(aTo - aFrom);
            for (let i = aFrom, j = 0; i < aTo; i++, j++) {
                subdata[j] = inputBufferAsUInt8Array[i];
            }

            return subdata.buffer;
        }
    }

    public loadData(aBuffer: Uint8Array) {
        let data: any = {
            positions: null,
            normals: null,
            texCoords: null,
            indices: null
        };

        let bpe = getBytesPerElementFromType("Float32");

        let offset = this.properties.offset;
        if (aBuffer === null || aBuffer.length === 0) { // load data from local properties
            data.positions = new Float32Array(this.properties.positions);
        } else {
            data.positions = new Float32Array(Binary.getSubBuffer(aBuffer, offset, offset + this.properties.vertexCount * 3 * bpe));
        }
        offset += this.properties.vertexCount * 3 * bpe;

        if (aBuffer === null || aBuffer.length === 0) { // load data from local properties
            data.normals = new Float32Array(this.properties.normals);
        }
        else {
            data.normals = new Float32Array(Binary.getSubBuffer(aBuffer, offset, offset + this.properties.normalCount * 3 * bpe));
        }
        offset += this.properties.normalCount * 3 * bpe;

        if (aBuffer === null || aBuffer.length === 0) { // load data from local properties
            data.texCoords = new Float32Array(this.properties.texCoords);
        }
        else {
            data.texCoords = new Float32Array(Binary.getSubBuffer(aBuffer, offset, offset + this.properties.texcoordCount * 2 * bpe));
        }
        offset += this.properties.texcoordCount * 2 * bpe;

        if (aBuffer === null || aBuffer.length === 0) {
            data.indices = new Uint32Array(this.properties.indices);
        } else {
            data.indices = new Uint32Array(Binary.getSubBuffer(aBuffer, offset, offset + this.properties.indexCount * 1 * 4));
        }

        // transform positions and normals when object is a repetition with transformation
        let repetitionTrafo = this.object.getElement().getRepetitionTransformation();
        if (repetitionTrafo) {
            data.positions =
                AbstractGeometry.transformArray(
                    data.positions,
                    this.properties.vertexCount,
                    new Float32Array(repetitionTrafo)
                );

            let rotationMat = mat4.clone(new Float32Array(repetitionTrafo));
            rotationMat[3] = 0.0;
            rotationMat[7] = 0.0;
            rotationMat[11] = 0.0;
            rotationMat[12] = 0.0;
            rotationMat[13] = 0.0;
            rotationMat[14] = 0.0;
            rotationMat[15] = 1.0;
            data.normals =
                AbstractGeometry.transformArray(
                    data.normals,
                    this.properties.normalCount,
                    rotationMat
                );
        }

        this.setDescriptors(data);
    }

    protected setDescriptors(aData) {
        // fill mesh with data
        let positionAttributeSize = this.object.getPositionAttributeSize();
        this.vertexCount = aData.positions.byteLength / (positionAttributeSize * 4);

        this.positionsDesc = new VertexDataDescriptor(
            "positions",
            positionAttributeSize,
            aData.positions.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            aData.positions
        );

        this.normalsDesc = new VertexDataDescriptor(
            "normals",
            3,
            aData.normals.length / 3,
            "FLOAT",
            4,
            0,
            aData.normals
        );

        this.texCoordsDesc = new VertexDataDescriptor(
            "texCoords",
            2,
            aData.texCoords.length / 2,
            "FLOAT",
            4,
            0,
            aData.texCoords
        );

        this.indicesDesc = new VertexDataDescriptor(
            "indices",
            1,
            aData.indices.length,
            "UINT",
            4,
            0,
            aData.indices
        );

        this.numIndices = aData.indices.length;
        this.numFaces = aData.indices.length / this.object.getPrimitiveSize();

        let nonIndexedVertices = this.generateNonIndexedVertices(positionAttributeSize);
        this.nonIndexedPositionsDesc = new VertexDataDescriptor(
            "nonIndexedPositions",
            positionAttributeSize,
            nonIndexedVertices.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            nonIndexedVertices
        );
    }

    public transform(aTransformation) {
        AbstractGeometry.prototype.transform.call(this, aTransformation);

        let positionAttributeSize = this.object.getPositionAttributeSize();
        let nonIndexedVertices = this.generateNonIndexedVertices(positionAttributeSize);
        this.nonIndexedPositionsDesc = new VertexDataDescriptor(
            "nonIndexedPositions",
            positionAttributeSize,
            nonIndexedVertices.length / positionAttributeSize,
            "FLOAT",
            4,
            0,
            nonIndexedVertices
        );
    }


    public toBase64(): string {

        // vertices
        let vertices = new Float32Array(this.positionsDesc.nofElements * 3);
        for (let i = 0; i < this.positionsDesc.nofElements * 3; i++) {
            vertices[i] = this.positionsDesc.data[i];
        }

        // normals
        let normals = new Float32Array(this.positionsDesc.nofElements * 3);
        for (let i = 0; i < this.positionsDesc.nofElements * 3; i++) {
            normals[i] = this.normalsDesc.data[i];
        }

        // tex coords
        let texCoords = new Float32Array(this.texCoordsDesc.nofElements * 2);
        for (let i = 0; i < this.texCoordsDesc.nofElements * 2; i++) {
            texCoords[i] = this.texCoordsDesc.data[i];
        }

        // indices
        let indices = new Uint32Array(this.indicesDesc.nofElements);
        for (let i = 0; i < this.indicesDesc.nofElements; i++) {
            indices[i] = this.indicesDesc.data[i];
        }

        let binnaryData = mergeBuffers(vertices.buffer, normals.buffer);
        binnaryData = mergeBuffers(binnaryData.buffer, texCoords.buffer);
        binnaryData = mergeBuffers(binnaryData.buffer, indices.buffer);

        return window.btoa(String.fromCharCode.apply(null, binnaryData));
    }

    public getJSONRepresentation(): any {
        let abstractProps = Binary.getDefaultProperties();
        let exportedProps = {};

        for (let member in abstractProps) {
            exportedProps[member] = this.properties[member];
        }

        return exportedProps;
    }
}

Factory.register(Binary);
